import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function PulsingRing({ delay = 0, size = 'h-32 w-32', color = 'border-indigo-400/30' }) {
  return (
    <motion.div
      className={`absolute rounded-full border-2 ${size} ${color}`}
      initial={{ opacity: 0.6, scale: 0.85 }}
      animate={{ opacity: 0, scale: 1.6 }}
      transition={{ duration: 2.2, repeat: Infinity, delay, ease: 'easeOut' }}
    />
  );
}

function SpinnerRing({ className = '' }) {
  return (
    <svg viewBox="0 0 56 56" fill="none" className={className}>
      <circle cx="28" cy="28" r="24" stroke="currentColor" strokeWidth="3" strokeOpacity="0.12" />
      <motion.circle
        cx="28" cy="28" r="24"
        stroke="url(#spinGrad)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="150.8"
        strokeDashoffset="113"
        animate={{ rotate: 360 }}
        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '28px 28px' }}
      />
      <defs>
        <linearGradient id="spinGrad" x1="0" y1="0" x2="56" y2="56" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6366f1" />
          <stop offset="1" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
    </svg>
  );
}

const logsContainerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04 } },
};
const logLineVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } },
};

export default function LoadingComponent({
  label = 'Loading Avenor...',
  subtitle = 'Preparing your lead workspace',
  progress = null,
  stage = '',
  detail = '',
  logs = [],
  children = null,
  fullBleed = false,
}) {
  const hasProgress = Number.isFinite(Number(progress));
  const pct = Math.max(0, Math.min(100, Number(progress) || 0));
  const hasJobContext = hasProgress || stage || detail || (Array.isArray(logs) && logs.length > 0) || Boolean(children);

  const logsEndRef = useRef(null);
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  return (
    <div className={`fixed inset-0 overflow-x-hidden bg-[radial-gradient(ellipse_at_top_left,_#ede9fe_0%,_#e0f2fe_45%,_#d1fae5_100%)] ${fullBleed ? 'overflow-y-auto px-0' : 'flex items-center justify-center overflow-y-auto px-4'}`}>
      {/* Background orbs */}
      <div className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-violet-400/20 blur-[80px]" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-sky-400/20 blur-[80px]" />
      <div className="pointer-events-none absolute bottom-1/3 left-1/4 h-72 w-72 rounded-full bg-emerald-400/15 blur-[60px]" />

      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className={`relative z-10 w-full border border-white/70 bg-white/60 shadow-[0_32px_90px_rgba(99,60,220,0.22)] backdrop-blur-2xl ${
          fullBleed
            ? 'mx-auto flex min-h-full max-w-[1600px] flex-col rounded-none px-4 py-5 sm:px-6 sm:py-7 lg:px-8 lg:py-8'
            : hasJobContext
            ? 'max-w-2xl rounded-3xl px-6 py-8 sm:px-10 sm:py-10'
            : 'max-w-sm rounded-3xl px-8 py-10'
        }`}
      >
        {/* Top accent line */}
        <div className="absolute inset-x-0 top-0 h-px rounded-t-3xl bg-gradient-to-r from-transparent via-indigo-400/60 to-transparent" />

        <div className={`grid gap-8 ${fullBleed ? 'flex-1 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)] lg:items-start' : 'text-center'}`}>
          <div className={`${fullBleed ? 'flex flex-col pt-2 lg:sticky lg:top-8 lg:justify-center' : ''}`}>
            {/* Spinner + pulses */}
            <div className={`relative ${fullBleed ? 'mx-0 mb-6' : 'mx-auto mb-6'} flex items-center justify-center`} style={{ width: 88, height: 88 }}>
              <PulsingRing delay={0}    size="h-[88px] w-[88px]"  color="border-indigo-400/25" />
              <PulsingRing delay={0.7}  size="h-[88px] w-[88px]"  color="border-violet-400/20" />
              <PulsingRing delay={1.4}  size="h-[88px] w-[88px]"  color="border-sky-400/15" />

              <div className="relative z-10 flex h-[72px] w-[72px] items-center justify-center rounded-2xl border border-white/70 bg-white/80 shadow-[0_8px_28px_rgba(99,60,220,0.25)]">
                <SpinnerRing className="absolute inset-0 h-full w-full text-indigo-600" />
                <img
                  src="/avenor.png"
                  alt="Avenor"
                  className="relative z-10 h-9 w-9 rounded-lg object-contain"
                />
              </div>
            </div>

            {/* Text */}
            <div className={fullBleed ? 'text-left' : 'text-center'}>
              <motion.p
                key={label}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-xl font-extrabold tracking-tight text-slate-900"
              >
                {label}
              </motion.p>
              <motion.p
                key={stage || subtitle}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="mt-1.5 text-sm font-medium text-slate-500"
              >
                {stage || subtitle}
              </motion.p>
              {detail && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="mt-2 inline-block rounded-full bg-indigo-50 px-3 py-0.5 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600"
                >
                  {detail}
                </motion.p>
              )}
            </div>
          </div>

          {/* Job context */}
          {hasJobContext && (
            <div className={`space-y-5 ${fullBleed ? 'min-w-0' : 'mt-7'}`}>
              {/* Progress bar */}
              {hasProgress && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Progress</span>
                    <motion.span
                      key={pct}
                      initial={{ scale: 1.3, color: '#6366f1' }}
                      animate={{ scale: 1, color: '#475569' }}
                      transition={{ duration: 0.3 }}
                      className="text-xs font-extrabold text-slate-600"
                    >
                      {pct}%
                    </motion.span>
                  </div>
                  <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-200/70 shadow-inner">
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-indigo-500 via-violet-500 to-sky-500"
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6, ease: [0.34, 1.26, 0.64, 1] }}
                    />
                    {/* Shimmer */}
                    <motion.div
                      className="absolute inset-y-0 w-16 rounded-full bg-white/30 blur-sm"
                      animate={{ left: ['-10%', '110%'] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.6 }}
                    />
                  </div>
                </div>
              )}

              {children && <div className="min-w-0">{children}</div>}

              {/* Logs */}
              {Array.isArray(logs) && logs.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-white/70 bg-slate-900/90 shadow-[0_12px_35px_rgba(15,23,42,0.3)]">
                  <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <span className="ml-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Queue Log</span>
                  </div>
                  <motion.div
                    className="max-h-48 space-y-1 overflow-y-auto px-4 py-3 font-mono text-xs leading-relaxed"
                    variants={logsContainerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {logs.map((logLine, index) => (
                      <motion.p
                        key={`${index}-${logLine}`}
                        variants={logLineVariants}
                        className="break-words text-emerald-300"
                      >
                        <span className="mr-2 text-slate-500 select-none">›</span>{logLine}
                      </motion.p>
                    ))}
                    <div ref={logsEndRef} />
                  </motion.div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
